import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../components/ui/Button';
import SalesCatalogModal from './components/SalesCatalogModal';
import SalesCatalogDetailsModal from './components/SalesCatalogDetailsModal';
import PurchasedItemsModal from './components/PurchasedItemsModal';
import PurchasedItemsDetailsModal from './components/PurchasedItemsDetailsModal';
import { API_ENDPOINTS } from '../../../utils/config';
import { 
  Warehouse, 
  Package,
  ShoppingCart,
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle
} from 'lucide-react';

const Inventory = () => {
  const [activeTab, setActiveTab] = useState('sales-catalog');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sales Catalog state
  const [catalogItems, setCatalogItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [itemDetails, setItemDetails] = useState(null);
  const [detailsError, setDetailsError] = useState('');
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [viewingItemId, setViewingItemId] = useState(null);
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [prefetchingItemId, setPrefetchingItemId] = useState(null);
  const [itemPendingDelete, setItemPendingDelete] = useState(null);
  
  // Purchased Items state
  const [purchasedItems, setPurchasedItems] = useState([]);
  const [isLoadingPurchased, setIsLoadingPurchased] = useState(false);
  const [isPurchasedModalOpen, setIsPurchasedModalOpen] = useState(false);
  const [selectedPurchasedItem, setSelectedPurchasedItem] = useState(null);
  const [purchasedModalMode, setPurchasedModalMode] = useState('add');
  const [isPurchasedDetailsModalOpen, setIsPurchasedDetailsModalOpen] = useState(false);
  const [purchasedItemDetails, setPurchasedItemDetails] = useState(null);
  const [purchasedDetailsError, setPurchasedDetailsError] = useState('');
  const [isPurchasedDetailsLoading, setIsPurchasedDetailsLoading] = useState(false);
  const [viewingPurchasedItemId, setViewingPurchasedItemId] = useState(null);
  const [deletingPurchasedItemId, setDeletingPurchasedItemId] = useState(null);
  const [prefetchingPurchasedItemId, setPrefetchingPurchasedItemId] = useState(null);
  const [purchasedItemPendingDelete, setPurchasedItemPendingDelete] = useState(null);
  
  const [notification, setNotification] = useState(null);

  const getAuthHeaders = (includeJson = false) => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const resolveUrl = (url, baseUrl) => {
    if (!url) return null;
    try {
      const base = baseUrl || API_ENDPOINTS.SALES_CATALOG_LIST;
      return new URL(url, base).toString();
    } catch (error) {
      return null;
    }
  };

  const extractItemsFromPayload = (payload) => {
    if (!payload) return [];
    // Direct array response (most common case)
    if (Array.isArray(payload)) return payload;
    // Paginated response with results
    if (Array.isArray(payload.results)) return payload.results;
    // Nested data structure
    if (payload.data) {
      if (Array.isArray(payload.data)) return payload.data;
      if (Array.isArray(payload.data.results)) return payload.data.results;
      // Single item wrapped in data
      if (payload.data.id && (payload.data.catalog_data || payload.data.goods_data || payload.data.services_data || payload.data.purchased_data || payload.data.category_type)) {
        return [payload.data];
      }
    }
    // Single item (not in array) - check for catalog_data, goods_data, services_data, purchased_data, or category_type
    if (payload.id && (payload.catalog_data || payload.goods_data || payload.services_data || payload.purchased_data || payload.category_type)) {
      return [payload];
    }
    // Other possible structures
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.catalog_items)) return payload.catalog_items;
    return [];
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    const parsedDate = new Date(dateString);
    return Number.isNaN(parsedDate.getTime()) ? 'N/A' : parsedDate.toLocaleDateString();
  };

  const loadItemDetails = async (itemId) => {
    const response = await axios.get(
      API_ENDPOINTS.SALES_CATALOG_DETAIL(itemId),
      { headers: getAuthHeaders() }
    );
    return response.data;
  };

  useEffect(() => {
    if (activeTab === 'sales-catalog') {
      fetchCatalogItems();
    } else if (activeTab === 'purchased-items') {
      fetchPurchasedItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (notification?.message) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchCatalogItems = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.SALES_CATALOG_LIST, { headers });
      
      // Extract items from response
      let items = extractItemsFromPayload(response.data);
      
      // If no items found, try different extraction methods
      if (items.length === 0) {
        // Check if response.data itself is an array
        if (Array.isArray(response.data)) {
          items = response.data;
        }
        // Check if response.data.data exists
        else if (response.data?.data) {
          if (Array.isArray(response.data.data)) {
            items = response.data.data;
          } else if (response.data.data.id) {
            items = [response.data.data];
          }
        }
        // Check if response has a different structure
        else if (response.data && typeof response.data === 'object') {
          // Try to find any array property
          for (const key in response.data) {
            if (Array.isArray(response.data[key]) && response.data[key].length > 0) {
              items = response.data[key];
              break;
            }
          }
        }
      }
      
      // Handle pagination if present
      if (items.length > 0 && (response.data?.next || response.data?.links?.next)) {
        let url = resolveUrl(response.data?.next || response.data?.links?.next, API_ENDPOINTS.SALES_CATALOG_LIST);
        let safetyCounter = 0;
        const MAX_PAGES = 20;
        
        while (url && safetyCounter < MAX_PAGES) {
          const pageResponse = await axios.get(url, { headers });
          const pageItems = extractItemsFromPayload(pageResponse.data);
          if (pageItems.length > 0) {
            items = items.concat(pageItems);
          }
          
          const nextUrl = pageResponse.data?.next || pageResponse.data?.links?.next || pageResponse.data?.pagination?.next;
          url = resolveUrl(nextUrl, API_ENDPOINTS.SALES_CATALOG_LIST);
          if (!url) {
            break;
          }
          safetyCounter += 1;
        }
      }
      
      setCatalogItems(items);
    } catch (error) {
      setCatalogItems([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Unable to load catalog items.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = () => {
    setSelectedItem(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleEditItem = async (item) => {
    if (!item?.id) return;
    setPrefetchingItemId(item.id);
    try {
      const detailedItem = await loadItemDetails(item.id);
      setSelectedItem(detailedItem);
      setModalMode('edit');
      setIsModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load item details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingItemId(null);
    }
  };

  const handleItemSave = () => {
    fetchCatalogItems();
    setNotification({
      type: 'success',
      message: modalMode === 'edit' ? 'Sales catalog item updated successfully.' : 'Sales catalog item created successfully.'
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleViewItem = async (itemId) => {
    if (!itemId) return;
    setViewingItemId(itemId);
    setIsDetailsModalOpen(true);
    setIsDetailsLoading(true);
    setDetailsError('');
    setItemDetails(null);
    try {
      const detail = await loadItemDetails(itemId);
      setItemDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch item details.';
      setDetailsError(apiError);
    } finally {
      setIsDetailsLoading(false);
      setViewingItemId(null);
    }
  };

  const handleCloseDetails = () => {
    setIsDetailsModalOpen(false);
    setItemDetails(null);
    setDetailsError('');
  };

  const handleDeleteItemRequest = (item) => {
    if (!item?.id) return;
    setItemPendingDelete(item);
  };

  const handleDeleteItemConfirm = async () => {
    const itemId = itemPendingDelete?.id;
    if (!itemId) return;

    setDeletingItemId(itemId);
    try {
      await axios.delete(API_ENDPOINTS.SALES_CATALOG_DELETE(itemId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Sales catalog item deleted successfully.' });
      await fetchCatalogItems();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete item.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingItemId(null);
      setItemPendingDelete(null);
    }
  };

  // Purchased Items Functions
  const loadPurchasedItemDetails = async (itemId) => {
    const response = await axios.get(
      API_ENDPOINTS.PURCHASED_ITEMS_DETAIL(itemId),
      { headers: getAuthHeaders() }
    );
    return response.data;
  };

  const fetchPurchasedItems = async () => {
    setIsLoadingPurchased(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.PURCHASED_ITEMS_LIST, { headers });
      
      let items = extractItemsFromPayload(response.data);
      
      // Handle pagination if present
      if (items.length > 0 && (response.data?.next || response.data?.links?.next)) {
        let url = resolveUrl(response.data?.next || response.data?.links?.next, API_ENDPOINTS.PURCHASED_ITEMS_LIST);
        let safetyCounter = 0;
        const MAX_PAGES = 20;
        
        while (url && safetyCounter < MAX_PAGES) {
          const pageResponse = await axios.get(url, { headers });
          const pageItems = extractItemsFromPayload(pageResponse.data);
          if (pageItems.length > 0) {
            items = items.concat(pageItems);
          }
          
          const nextUrl = pageResponse.data?.next || pageResponse.data?.links?.next || pageResponse.data?.pagination?.next;
          url = resolveUrl(nextUrl, API_ENDPOINTS.PURCHASED_ITEMS_LIST);
          if (!url) {
            break;
          }
          safetyCounter += 1;
        }
      }
      
      setPurchasedItems(items);
    } catch (error) {
      setPurchasedItems([]);
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Unable to load purchased items.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setIsLoadingPurchased(false);
    }
  };

  const handleAddPurchasedItem = () => {
    setSelectedPurchasedItem(null);
    setPurchasedModalMode('add');
    setIsPurchasedModalOpen(true);
  };

  const handleEditPurchasedItem = async (item) => {
    if (!item?.id) return;
    setPrefetchingPurchasedItemId(item.id);
    try {
      const detailedItem = await loadPurchasedItemDetails(item.id);
      setSelectedPurchasedItem(detailedItem);
      setPurchasedModalMode('edit');
      setIsPurchasedModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 'Unable to load item details for editing.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setPrefetchingPurchasedItemId(null);
    }
  };

  const handlePurchasedItemSave = () => {
    fetchPurchasedItems();
    setNotification({
      type: 'success',
      message: purchasedModalMode === 'edit' ? 'Purchased item updated successfully.' : 'Purchased item created successfully.'
    });
  };

  const handleClosePurchasedModal = () => {
    setIsPurchasedModalOpen(false);
    setSelectedPurchasedItem(null);
  };

  const handleViewPurchasedItem = async (itemId) => {
    if (!itemId) return;
    setViewingPurchasedItemId(itemId);
    setIsPurchasedDetailsModalOpen(true);
    setIsPurchasedDetailsLoading(true);
    setPurchasedDetailsError('');
    setPurchasedItemDetails(null);
    try {
      const detail = await loadPurchasedItemDetails(itemId);
      setPurchasedItemDetails(detail);
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Unable to fetch item details.';
      setPurchasedDetailsError(apiError);
    } finally {
      setIsPurchasedDetailsLoading(false);
      setViewingPurchasedItemId(null);
    }
  };

  const handleClosePurchasedDetails = () => {
    setIsPurchasedDetailsModalOpen(false);
    setPurchasedItemDetails(null);
    setPurchasedDetailsError('');
  };

  const handleDeletePurchasedItemRequest = (item) => {
    if (!item?.id) return;
    setPurchasedItemPendingDelete(item);
  };

  const handleDeletePurchasedItemConfirm = async () => {
    const itemId = purchasedItemPendingDelete?.id;
    if (!itemId) return;

    setDeletingPurchasedItemId(itemId);
    try {
      await axios.delete(API_ENDPOINTS.PURCHASED_ITEMS_DELETE(itemId), {
        headers: getAuthHeaders()
      });
      setNotification({ type: 'success', message: 'Purchased item deleted successfully.' });
      await fetchPurchasedItems();
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete item.';
      setNotification({ type: 'error', message: apiError });
    } finally {
      setDeletingPurchasedItemId(null);
      setPurchasedItemPendingDelete(null);
    }
  };

  const renderSalesCatalogTab = () => {
    const activeItems = catalogItems.filter(i => i.is_active !== false).length;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredItems = catalogItems.filter((item) => {
      if (!normalizedSearch) return true;
      
      // Get catalog_data (new API structure) or fallback to goods_data/services_data
      const catalogData = item?.catalog_data || item?.goods_data || item?.services_data || {};
      const categoryType = item?.category_type || 'goods';
      
      const itemName = categoryType === 'goods' 
        ? (catalogData?.goods_name || catalogData?.service_name || '')
        : (catalogData?.service_name || catalogData?.goods_name || '');
      
      const searchableFields = [
        itemName,
        catalogData?.sku,
        catalogData?.hsn_code,
        catalogData?.sac_code,
        catalogData?.description,
        catalogData?.price,
        item?.id?.toString(),
        categoryType
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    const showNoItems = !isLoading && catalogItems.length === 0;
    const showNoMatches = !isLoading && catalogItems.length > 0 && filteredItems.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };
    
    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold text-foreground">{catalogItems.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {activeItems} Active
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Goods</p>
                <p className="text-2xl font-bold text-foreground">
                  {catalogItems.filter(i => i.category_type === 'goods').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Goods items</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Services</p>
                <p className="text-2xl font-bold text-foreground">
                  {catalogItems.filter(i => i.category_type === 'services').length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">Service items</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">
                  {Math.max(catalogItems.length - activeItems, 0)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Sales Catalog Management</h3>
            <Button onClick={handleAddItem}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search catalog items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            {/* <Button variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button> */}
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading catalog items...</p>
              </div>
            </div>
          ) : showNoItems ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Warehouse className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No catalog items yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by adding your first sales catalog item</p>
              <Button onClick={handleAddItem}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching items</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Price
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {filteredItems.some(i => i.category_type === 'goods') ? 'HSN/SAC' : 'SAC Code'}
                    </th> */}
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Stock
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => {
                    // Get category type
                    const categoryType = item?.category_type || 'goods';
                    
                    // Get catalog_data (new API structure) or fallback to goods_data/services_data
                    const catalogData = item?.catalog_data || item?.goods_data || item?.services_data || {};
                    
                    const itemName = categoryType === 'goods' 
                      ? (catalogData?.goods_name || catalogData?.service_name || 'N/A')
                      : (catalogData?.service_name || catalogData?.goods_name || 'N/A');
                    const avatarSource = itemName || '??';
                    const avatar = avatarSource?.substring ? avatarSource.substring(0, 2).toUpperCase() : '??';
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{avatar}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {itemName || 'N/A'}
                              </div>
                              {categoryType === 'goods' && catalogData?.sku && (
                                <div className="text-sm text-muted-foreground">
                                  SKU: {catalogData.sku}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            categoryType === 'goods'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {categoryType === 'goods' ? 'Goods' : 'Services'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            ₹{catalogData?.price || '0'}
                          </div>
                        </td>
                        {/* <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {categoryType === 'goods' 
                              ? (catalogData?.hsn_code || 'N/A')
                              : (catalogData?.sac_code || 'N/A')}
                          </div>
                        </td> */}
                        {/* <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {categoryType === 'goods' 
                              ? (catalogData?.stock_quantity || 'N/A') 
                              : 'N/A'}
                          </div>
                        </td> */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.is_active !== false
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDisplayDate(item?.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              type="button"
                              title="View details"
                              aria-label="View item details"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleViewItem(item.id)}
                              disabled={viewingItemId === item.id}
                            >
                              {viewingItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View</span>
                                </>
                              )}
                            </button>
                            <button 
                              type="button"
                              title="Edit item"
                              aria-label="Edit item"
                              className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleEditItem(item)}
                              disabled={prefetchingItemId === item.id}
                            >
                              {prefetchingItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Edit className="w-3.5 h-3.5" />
                                  <span>Edit</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete item"
                              aria-label="Delete item"
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleDeleteItemRequest(item)}
                              disabled={deletingItemId === item.id}
                            >
                              {deletingItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPurchasedItemsTab = () => {
    const activeItems = purchasedItems.filter(i => i.is_active !== false).length;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredItems = purchasedItems.filter((item) => {
      if (!normalizedSearch) return true;
      
      const categoryType = item?.category_type || 'goods';
      const itemData = item?.purchased_data || {};
      
      const itemName = categoryType === 'goods' 
        ? (itemData?.item_name || '')
        : (itemData?.service_name || '');
      
      const searchableFields = [
        itemName,
        itemData?.supplier_name,
        itemData?.description,
        itemData?.unit_cost,
        itemData?.total_cost,
        itemData?.quantity_purchased,
        item?.id?.toString(),
        categoryType
      ];
      return searchableFields.some((field) => {
        if (!field) return false;
        return String(field).toLowerCase().includes(normalizedSearch);
      });
    });
    const showNoItems = !isLoadingPurchased && purchasedItems.length === 0;
    const showNoMatches = !isLoadingPurchased && purchasedItems.length > 0 && filteredItems.length === 0;

    const notificationStyles = notification?.type === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : notification?.type === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800';

    const renderNotificationIcon = () => {
      if (notification?.type === 'error') {
        return <AlertCircle className="w-5 h-5 mt-0.5" />;
      }
      if (notification?.type === 'success') {
        return <CheckCircle className="w-5 h-5 mt-0.5" />;
      }
      return <Info className="w-5 h-5 mt-0.5" />;
    };
    
    return (
      <div className="space-y-6">
        {notification?.message && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${notificationStyles}`}>
            {renderNotificationIcon()}
            <p className="text-sm">{notification.message}</p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold text-foreground">{purchasedItems.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-2">
              {activeItems} Active
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Goods</p>
                <p className="text-2xl font-bold text-foreground">
                  {purchasedItems.filter(i => i.category_type === 'goods').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-green-600 mt-2">Goods items</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Services</p>
                <p className="text-2xl font-bold text-foreground">
                  {purchasedItems.filter(i => i.category_type === 'services').length}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm text-purple-600 mt-2">Service items</p>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-2xl font-bold text-foreground">
                  {Math.max(purchasedItems.length - activeItems, 0)}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-sm text-orange-600 mt-2">Needs attention</p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Purchased Items Management</h3>
            <Button onClick={handleAddPurchasedItem}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search purchased items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {isLoadingPurchased ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Loading purchased items...</p>
              </div>
            </div>
          ) : showNoItems ? (
            <div className="flex flex-col items-center justify-center py-12">
              <ShoppingCart className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">No purchased items yet</p>
              <p className="text-sm text-muted-foreground mb-4">Get started by adding your first purchased item</p>
              <Button onClick={handleAddPurchasedItem}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          ) : showNoMatches ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <Search className="w-10 h-10 text-gray-400 mb-3" />
              <p className="text-lg font-medium text-gray-900 mb-1">No matching items</p>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search term to find what you need.
              </p>
              <Button variant="outline" onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => {
                    const categoryType = item?.category_type || 'goods';
                    const itemData = item?.purchased_data || {};
                    const itemName = categoryType === 'goods' 
                      ? (itemData?.item_name || 'N/A')
                      : (itemData?.service_name || 'N/A');
                    const avatarSource = itemName || '??';
                    const avatar = avatarSource?.substring ? avatarSource.substring(0, 2).toUpperCase() : '??';
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-sm">{avatar}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-foreground">
                                {itemName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            categoryType === 'goods'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {categoryType === 'goods' ? 'Goods' : 'Services'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            ₹{categoryType === 'goods' ? (itemData?.unit_cost || '0') : (itemData?.total_cost || '0')}
                          </div>
                          {categoryType === 'goods' && itemData?.quantity_purchased && (
                            <div className="text-xs text-muted-foreground">
                              Qty: {itemData.quantity_purchased}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {itemData?.supplier_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {formatDisplayDate(item?.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            item.is_active !== false
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {formatDisplayDate(item?.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-3">
                            <button
                              type="button"
                              title="View details"
                              aria-label="View item details"
                              className="inline-flex items-center gap-1 rounded-full border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleViewPurchasedItem(item.id)}
                              disabled={viewingPurchasedItemId === item.id}
                            >
                              {viewingPurchasedItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>View</span>
                                </>
                              )}
                            </button>
                            <button 
                              type="button"
                              title="Edit item"
                              aria-label="Edit item"
                              className="inline-flex items-center gap-1 rounded-full border border-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleEditPurchasedItem(item)}
                              disabled={prefetchingPurchasedItemId === item.id}
                            >
                              {prefetchingPurchasedItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-green-200 border-t-green-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Edit className="w-3.5 h-3.5" />
                                  <span>Edit</span>
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete item"
                              aria-label="Delete item"
                              className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleDeletePurchasedItemRequest(item)}
                              disabled={deletingPurchasedItemId === item.id}
                            >
                              {deletingPurchasedItemId === item.id ? (
                                <span className="inline-block w-4 h-4 border-2 border-red-200 border-t-red-600 rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Delete</span>
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 bg-primary rounded-lg">
            <Warehouse className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Inventory</h1>
        </div>
        <p className="text-muted-foreground">
          Manage your sales catalog and purchased items (goods and services)
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('sales-catalog')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'sales-catalog'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              <Package className="w-4 h-4" />
              <span>Sales Catalog</span>
            </button>
            <button
              onClick={() => setActiveTab('purchased-items')}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'purchased-items'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Purchased Items</span>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'sales-catalog' && renderSalesCatalogTab()}
          {activeTab === 'purchased-items' && renderPurchasedItemsTab()}
        </div>
      </div>

      {/* Modals */}
      <SalesCatalogModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        catalogItem={selectedItem}
        onSave={handleItemSave}
        mode={modalMode}
      />
      <SalesCatalogDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetails}
        catalogItem={itemDetails}
        isLoading={isDetailsLoading}
        error={detailsError}
      />
      {itemPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Item</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {(() => {
                      const categoryType = itemPendingDelete?.category_type || 'goods';
                      const catalogData = itemPendingDelete?.catalog_data || itemPendingDelete?.goods_data || itemPendingDelete?.services_data || {};
                      return categoryType === 'goods' 
                        ? (catalogData?.goods_name || catalogData?.service_name || 'Confirm deletion')
                        : (catalogData?.service_name || catalogData?.goods_name || 'Confirm deletion');
                    })()}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The catalog item and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Item details</p>
                <p className="text-sm text-gray-900">
                  {(() => {
                    const categoryType = itemPendingDelete?.category_type || 'goods';
                    const catalogData = itemPendingDelete?.catalog_data || itemPendingDelete?.goods_data || itemPendingDelete?.services_data || {};
                    return categoryType === 'goods' 
                      ? (catalogData?.goods_name || catalogData?.service_name || 'N/A')
                      : (catalogData?.service_name || catalogData?.goods_name || 'N/A');
                  })()}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  Type: {itemPendingDelete?.category_type || 'N/A'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingItemId === itemPendingDelete?.id}
                  onClick={() => setItemPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingItemId === itemPendingDelete?.id}
                  onClick={handleDeleteItemConfirm}
                >
                  {deletingItemId === itemPendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchased Items Modals */}
      <PurchasedItemsModal
        isOpen={isPurchasedModalOpen}
        onClose={handleClosePurchasedModal}
        purchasedItem={selectedPurchasedItem}
        onSave={handlePurchasedItemSave}
        mode={purchasedModalMode}
      />
      <PurchasedItemsDetailsModal
        isOpen={isPurchasedDetailsModalOpen}
        onClose={handleClosePurchasedDetails}
        purchasedItem={purchasedItemDetails}
        isLoading={isPurchasedDetailsLoading}
        error={purchasedDetailsError}
      />
      {purchasedItemPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">Delete Item</p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {(() => {
                      const categoryType = purchasedItemPendingDelete?.category_type || 'goods';
                      const itemData = purchasedItemPendingDelete?.purchased_data || {};
                      return categoryType === 'goods' 
                        ? (itemData?.item_name || itemData?.service_name || 'Confirm deletion')
                        : (itemData?.service_name || itemData?.item_name || 'Confirm deletion');
                    })()}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The purchased item and its related data will be permanently removed.
              </p>
            </div>
            <div className="p-6 flex flex-col gap-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-semibold text-gray-500 tracking-wide uppercase mb-1">Item details</p>
                <p className="text-sm text-gray-900">
                  {(() => {
                    const categoryType = purchasedItemPendingDelete?.category_type || 'goods';
                    const itemData = purchasedItemPendingDelete?.purchased_data || {};
                    return categoryType === 'goods' 
                      ? (itemData?.item_name || itemData?.service_name || 'N/A')
                      : (itemData?.service_name || itemData?.item_name || 'N/A');
                  })()}
                </p>
                <p className="text-xs text-muted-foreground capitalize">
                  Type: {purchasedItemPendingDelete?.category_type || 'N/A'}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={deletingPurchasedItemId === purchasedItemPendingDelete?.id}
                  onClick={() => setPurchasedItemPendingDelete(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={deletingPurchasedItemId === purchasedItemPendingDelete?.id}
                  onClick={handleDeletePurchasedItemConfirm}
                >
                  {deletingPurchasedItemId === purchasedItemPendingDelete?.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
